import React, { useCallback, useEffect, useRef, useState } from "react";

import { Close, DeleteForever, Edit, LockReset, PersonAdd } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";

import { Form } from "@rjsf/mui";
import validator from "@rjsf/validator-ajv8";
import { JSONSchema7 } from "json-schema";

import PrimaryLayout from "../components/layout/PrimaryLayout";
import DataTable from "../components/tables/DataTable";
import { axiosKeycloakInstance } from "../services/api-client";

interface User {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  password?: string;
}

interface ResetPassword {
  username: string;
  newpassword: string;
  repeatepassword: string;
}

const ManageUsers: React.FC = () => {

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [formData, setFormData] = useState<User | null>(null);
  const [resetPasswordData, setResetPasswordData] = useState<ResetPassword | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState<boolean>(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [userToRestPassword, setUserToResetPassword] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [searchValue, setSearchValue] = useState<string>('');
  const [refreshKey, setRefreshKey] = useState<number>(0);

  const columns = [
    { label: "User Name", id: "username" },
    { label: "Email", id: "email" },
    { label: "Last Name", id: "lastName" },
    { label: "First Name", id: "firstName" },
    { label: "Actions", id: "actions" },
  ];

  const userSchema: JSONSchema7 = {
    type: "object",
    required: ["username", "email", "firstName", "lastName", ...(isEditMode ? [] : ["password"])],
    properties: {
      username: {
        type: "string",
        title: "Username",
        readOnly: isEditMode,
      },
      email: { type: "string", title: "Email", pattern: "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$" },
      firstName: { type: "string", title: "First Name", pattern: "^[A-Za-z]+$", },
      lastName: { type: "string", title: "Last Name", pattern: "^[A-Za-z]+$", },
      ...(isEditMode
        ? {}
        : {
          password: { type: "string", title: "Password" },
        }),
    },
  };

  const resetPasswordSchema: JSONSchema7 = {
    type: "object",
    required: ["username", "newpassword", "repeatepassword"],
    properties: {
      username: {
        type: "string",
        title: "Username",
        readOnly: true,
      },
      newpassword: { type: "string", title: "New Password" },
      repeatepassword: { type: "string", title: "Repeate Password" },
    }
  }

  const fetchData = useCallback(async (query?: | { pageSize?: number; nextPageToken?: string } | { page?: number; limit?: number }) => {
    if (query && ("page" in query || "limit" in query)) {
      try {
        const first = query.page ? (query.page - 1) * (query.limit || 0) : 0;
        const max = query.limit || 10;
        const search = searchValue;
        const response = await axiosKeycloakInstance.get("/users", { params: { first, max, search }, });
        return response.data;
      } catch (err: any) {
        console.error("Error fetching users:", err);
        return [];
      }
    } else {
      return [];
    }
  }, [searchValue])

  const dataExtractor = useCallback((response: any[]) => {
    return response.map((user: any) => ({
      id: user.id,
      username: user.username,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      actions: <Box display='flex' gap={1}>
        <Tooltip title='Edit User Details'>
          <IconButton color='info' onClick={() => {
            setFormData(user);
            setIsEditMode(true);
            setDrawerOpen(true);
          }}>
            <Edit />
          </IconButton>
        </Tooltip>
        <Tooltip title='Delete User Details' onClick={() => {
          setUserToDelete(user);
          setDeleteDialogOpen(true);
        }}>
          <IconButton color='error'><DeleteForever /></IconButton>
        </Tooltip>
        <Tooltip title='Reset Password' onClick={() => {
          setUserToResetPassword(user);
          setResetPasswordDialogOpen(true);
          setResetPasswordData({
            username: user.username,
            newpassword: "",
            repeatepassword: "",
          });
        }}>
          <IconButton color='warning'><LockReset /></IconButton>
        </Tooltip>
      </Box>
    }));
  }, [])

  const handleFormSubmit = async (formData: any) => {
    setIsLoading(true);

    try {
      const submittedData = formData.formData;
      const payload: any = {
        username: submittedData.username,
        firstName: submittedData.firstName,
        lastName: submittedData.lastName,
        email: submittedData.email,
        enabled: true,
      };

      if (!isEditMode && submittedData.password) {
        payload.credentials = [
          { type: "password", value: submittedData.password, temporary: false },
        ];
      }

      let response;

      if (isEditMode && formData.formData.id) {
        response = await axiosKeycloakInstance.put(`/users/${formData.formData.id}`, payload);
      } else {
        response = await axiosKeycloakInstance.post("/users", payload);
      }

      setDrawerOpen(false);
      if (response.status === 201 || response.status === 204) {
        setRefreshKey(prevKey => prevKey + 1);
      }
    } catch (err) {
      console.error("Error saving user:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPasswordFormSubmint = async (formData: any) => {
    // console.log("handleResetPasswordFormSubmint", formData);
    if (formData.formData.newpassword === formData.formData.repeatepassword) {
      await handleResetPassword(formData.formData.newpassword || "");
      setResetPasswordDialogOpen(false);
    } else {
      alert("New Password and Confirm Password not matched..");
    }
  }

  const handleDeleteUser = async () => {
    if (userToDelete) {
      setIsLoading(true);
      try {
        const response = await axiosKeycloakInstance.delete(`/users/${userToDelete.id}`);
        setDeleteDialogOpen(false);
        if (response.status === 204) {
          setRefreshKey(prevKey => prevKey + 1);
        }
      } catch (err) {
        console.error("Error deleting user:", err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleResetPassword = async (newpassword: string) => {
    if (userToRestPassword) {
      setIsLoading(true);
      const payload = {
        type: "password",
        value: newpassword,
        temporary: false,
      };
      try {
        const response = await axiosKeycloakInstance.put(`/users/${userToRestPassword.id}/reset-password`, payload);
        if (response.status === 204) {
          setRefreshKey(prevKey => prevKey + 1);
        }
      } catch (error) {
        console.error("Error while Resetting the password: ", error);
      } finally {
        setIsLoading(false);
      }
    }
  }

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    debounceRef.current = setTimeout(() => {
      setSearchValue(searchTerm)
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    }
  }, [searchTerm])


  return (
    <PrimaryLayout transparentLoading={isLoading}>
      <Box display="flex" justifyContent="space-between" alignItems="center" m={2}>
        <TextField
          fullWidth
          variant="outlined"
          label="Search Users"
          placeholder="Search text..."
          sx={{ width: 320 }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoComplete="off"
          type="search"
          helperText='Search with username, first or last name, or email'
        />
        <Button
          variant="contained"
          color="primary"
          endIcon={<PersonAdd />}
          onClick={() => {
            setFormData(null);
            setIsEditMode(false);
            setDrawerOpen(true);
          }}
        >
          Add User
        </Button>
      </Box>

      <Box key={refreshKey}>
        <DataTable
          columns={columns}
          fetchData={fetchData}
          dataExtractor={dataExtractor}
        />
      </Box>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete user with username as {userToDelete?.username}?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" onClick={handleDeleteUser}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer anchor="right" open={resetPasswordDialogOpen} onClose={() => setResetPasswordDialogOpen(false)}>
        <Box sx={{ width: 400, p: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Reset User Password</Typography>
            <IconButton onClick={() => setResetPasswordDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
          <Form
            schema={resetPasswordSchema}
            formData={resetPasswordData || {}}
            showErrorList={false}
            autoComplete="off"
            onSubmit={handleResetPasswordFormSubmint}
            onError={(errors) => console.log("Form errors:", errors)}
            validator={validator}
          />
        </Box>
      </Drawer>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 400, p: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">{isEditMode ? "Edit User" : "Add User"}</Typography>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <Close />
            </IconButton>
          </Box>
          <Form
            schema={userSchema}
            formData={formData || {}}
            showErrorList={false}
            onSubmit={handleFormSubmit}
            autoComplete="off"
            onError={(errors) => console.log("Form errors:", errors)}
            validator={validator}
            transformErrors={(errors) => {
              return errors.map((error) => {
                if (error.name === "pattern" && error.property === ".email") {
                  error.message = "Please provide a valid email id"
                } else if (error.name === "pattern" && error.property === ".firstName") {
                  error.message = "Please use capital or small case alphabets only for First Name.";
                } else if (error.name === "pattern" && error.property === ".lastName") {
                  error.message = "Please use capital or small case alphabets only for Last Name.";
                }
                return error;
              });
            }}
          />
        </Box>
      </Drawer>
    </PrimaryLayout>
  );
};

export default ManageUsers;
